import {
  istLiegengeblieben,
  markierKlasse,
  markierTage,
  markierWort,
  MARKIER_FILTER,
  MARKIER_FILTER_LABELS,
  MARKIER_STUFE_LABELS,
  passtZuMarkierFilter,
  tagKurz,
  type MarkierFilter,
} from '@netcon-work/shared'
import { Sun } from 'lucide-react'
import {
  heuteSchluessel,
  useDarfMarkieren,
  useMarkieren,
  useMarkierungen,
  useOffeneMarkierungen,
} from '../lib/markierung'

/**
 * **Die Sonne und die drei Knöpfe** – die Bauteile, aus denen jede Ansicht ihre Markierung
 * zusammensetzt ([Z-20](../../../../docs/phasen/zusatz-20-mein-tag-markieren.md)).
 *
 * Fünf Ansichten zeigen dieselbe Sache (Detail, Vorlauf, Liste, Telefon, Kalender). Stünde die
 * Sonne fünfmal im JSX, hätte sie beim ersten Liegengebliebenen fünf verschiedene Wörter.
 */

/**
 * **☀ Heute · ☀ Morgen · ☀ Do., 08.10.** – die Marke an einer Aufgabe.
 *
 * Eine liegengebliebene Markierung trägt „seit Fr." und einen gestrichelten Rand: sie gilt
 * weiter ([F-95](../../../../docs/OFFENE-FRAGEN.md#f-95)), soll aber nicht aussehen, als wäre sie
 * heute gesetzt. Ohne Markierung: nichts – eine leere Marke wäre Rauschen in jeder Zeile.
 */
export function SonnenMarke({ taskId, klein }: { taskId: string; klein?: boolean }) {
  const markierungen = useMarkierungen()
  const am = markierungen.get(taskId)
  if (!am) return null
  return <SonnenMarkeFuer am={am} klein={klein} />
}

/** Dieselbe Marke für einen bekannten Tag – für Listen, die die Tabelle schon in der Hand haben. */
export function SonnenMarkeFuer({ am, klein }: { am: string; klein?: boolean | undefined }) {
  const heute = heuteSchluessel()
  const alt = istLiegengeblieben(am, heute)
  return (
    <span
      title={`Für Mein Tag markiert: ${tagKurz(am)}`}
      className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-sonne-line bg-sonne-bg font-semibold text-sonne ${
        alt ? 'border-dashed' : ''
      } ${klein ? 'px-1.5 text-[10.5px]' : 'px-2 py-px text-[11px]'}`}
    >
      <Sun size={klein ? 10 : 11} aria-hidden />
      {markierWort(am, heute)}
    </span>
  )
}

/**
 * **Heute · Morgen · Übermorgen · ✕** – die Segmentleiste, im Stil der Zeitfenster.
 *
 * Unter jedem Knopf steht im Titel der echte Tag, und am Detail auch sichtbar daneben (`mitTag`):
 * freitags heißt „Morgen" Montag, und das soll niemand nachrechnen müssen. Ein zweiter Klick auf
 * die gewählte Stufe nimmt die Markierung weg – wie bei Zeitfenster und Größe.
 *
 * `taskIds` statt einer Kennung: dieselbe Leiste trägt auch die Sammelaktion der Listen.
 */
export function MarkierKnoepfe({
  taskIds,
  mitTag,
  mitDatum,
  kompakt,
}: {
  taskIds: string[]
  /** Den echten Tag neben dem Wort zeigen („Morgen Di., 06.10."). */
  mitTag?: boolean
  /** Ein Datumsfeld für alles weiter weg. */
  mitDatum?: boolean
  /** Ohne „Übermorgen" ausgeschrieben – für enge Zeilen. */
  kompakt?: boolean
}) {
  const darf = useDarfMarkieren()
  const markierungen = useMarkierungen()
  const markieren = useMarkieren()
  if (!darf || taskIds.length === 0) return null

  const heute = heuteSchluessel()
  /** Bei einer einzelnen Aufgabe: welche Stufe ist gewählt? Bei mehreren: keine. */
  const am = taskIds.length === 1 ? markierungen.get(taskIds[0]!) : undefined
  const gewaehlt = am ? markierKlasse(am, heute) : null

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="inline-flex overflow-hidden rounded-lg border border-line2 text-[11.5px]">
        {markierTage(heute).map(({ stufe, tag }, i) => {
          const aktiv = gewaehlt === stufe
          return (
            <button
              key={stufe}
              type="button"
              aria-pressed={aktiv}
              title={`Für Mein Tag markieren: ${tagKurz(tag)}`}
              onClick={() => markieren.mutate({ taskIds, am: aktiv ? null : tag })}
              className={`cursor-pointer px-2 py-[3px] font-semibold ${i > 0 ? 'border-l border-line2' : ''} ${
                aktiv ? 'bg-sonne-bg text-sonne' : 'text-ink2 hover:bg-[#f3f2ef]'
              }`}
            >
              {i === 0 && <Sun size={11} className="mr-1 inline align-[-1px]" aria-hidden />}
              {kompakt && stufe === 'uebermorgen' ? 'Überm.' : MARKIER_STUFE_LABELS[stufe]}
              {mitTag && ' '}
              {mitTag && (
                <span className={`font-normal ${aktiv ? 'text-sonne' : 'text-muted'}`}>
                  {tagKurz(tag).split(', ')[1]}
                </span>
              )}
            </button>
          )
        })}
        {(am || taskIds.length > 1) && (
          <button
            type="button"
            title="Markierung entfernen"
            aria-label="Markierung entfernen"
            onClick={() => markieren.mutate({ taskIds, am: null })}
            className="cursor-pointer border-l border-line2 px-2 py-[3px] text-muted hover:bg-[#f3f2ef] hover:text-bad"
          >
            ✕
          </button>
        )}
      </div>
      {mitDatum && (
        <input
          type="date"
          aria-label="Für einen anderen Tag markieren"
          min={heute}
          value={am && gewaehlt === 'spaeter' ? am : ''}
          onChange={(e) => {
            if (e.target.value) markieren.mutate({ taskIds, am: e.target.value })
          }}
          className="rounded-md border border-line2 bg-surface px-1.5 py-[2px] text-[11.5px] text-ink2"
        />
      )}
      {markieren.isError && (
        <span className="text-[11px] text-bad">Nicht gespeichert – bitte nochmal.</span>
      )}
    </div>
  )
}

/**
 * **Die Auswahl in einer Tabellenzeile** – „Alle Aufgaben", Spalte „Mein Tag" (Z-20).
 *
 * Eine native Auswahl statt der Segmentleiste: in einer Zeile von 110 px passen drei Knöpfe nicht,
 * und eine Auswahl ist mit der Tastatur ohnehin der kürzeste Weg. Gesetzt ist sie in Sonnenfarbe,
 * leer bleibt sie blass – so sieht man beim Überfliegen, welche Zeilen markiert sind.
 */
export function MarkierAuswahl({ taskId, titel }: { taskId: string; titel: string }) {
  const darf = useDarfMarkieren()
  const markierungen = useMarkierungen()
  const markieren = useMarkieren()
  if (!darf) return <span className="text-muted">–</span>

  const heute = heuteSchluessel()
  const am = markierungen.get(taskId)
  const wirksam = am ? (istLiegengeblieben(am, heute) ? heute : am) : ''
  const tage = markierTage(heute)
  /** Ein Tag hinter Übermorgen steht als eigene Zeile da, sonst zeigte die Auswahl „–". */
  const weiter = wirksam && !tage.some((t) => t.tag === wirksam) ? wirksam : null

  return (
    <select
      aria-label={`Mein Tag für ${titel}`}
      value={wirksam}
      onChange={(e) => markieren.mutate({ taskIds: [taskId], am: e.target.value || null })}
      className={`w-full cursor-pointer rounded-md border px-1 py-[2px] text-[11.5px] ${
        am
          ? 'border-sonne-line bg-sonne-bg font-semibold text-sonne'
          : 'border-line2 bg-surface text-muted'
      }`}
    >
      <option value="">–</option>
      {tage.map(({ stufe, tag }) => (
        <option key={stufe} value={tag}>
          {MARKIER_STUFE_LABELS[stufe]}
          {am && istLiegengeblieben(am, heute) && stufe === 'heute'
            ? ` (seit ${tagKurz(am).split(',')[0]})`
            : ''}
        </option>
      ))}
      {weiter && <option value={weiter}>{tagKurz(weiter)}</option>}
    </select>
  )
}

/**
 * **„☀ Mein Tag: Markiert · Heute · Morgen · Übermorgen · Später · Ohne"** – die Filterreihe
 * (Z-20), dieselbe in „Alle Aufgaben", Pools und Vorhaben.
 *
 * Gezählt wird über `taskIds` – den ganzen Bestand der Ansicht, nicht die gefilterte Sicht, sonst
 * zeigte der Knopf seine eigene Wirkung an. Ein Knopf erscheint nur mit Zahl größer null (wie
 * „liegengeblieben"); „Ohne" trägt keine Zahl, sie wäre fast der ganze Bestand. Ist gar nichts
 * markiert, fehlt die ganze Reihe – solange nicht gerade ein Filter gesetzt ist, der sonst nicht
 * mehr zurückzunehmen wäre.
 */
export function MarkierFilterLeiste({
  taskIds,
  wert,
  setzen,
}: {
  taskIds: readonly string[]
  wert: MarkierFilter | 'alle'
  setzen: (wert: MarkierFilter | 'alle') => void
}) {
  const darf = useDarfMarkieren()
  const markierungen = useOffeneMarkierungen()
  if (!darf) return null
  const heute = heuteSchluessel()
  const zahlen = new Map(
    MARKIER_FILTER.map((f) => [
      f,
      taskIds.filter((id) => passtZuMarkierFilter(markierungen.get(id) ?? null, heute, f)).length,
    ]),
  )
  if ((zahlen.get('markiert') ?? 0) === 0 && wert === 'alle') return null

  return (
    <div
      className="flex flex-wrap items-center gap-1"
      role="group"
      aria-label="Nach Mein Tag filtern"
    >
      <span className="flex items-center gap-1 text-[12px] text-muted">
        <Sun size={12} className="text-sonne" aria-hidden /> Mein Tag:
      </span>
      {MARKIER_FILTER.map((f) => {
        const zahl = zahlen.get(f) ?? 0
        if (f !== 'ohne' && zahl === 0 && wert !== f) return null
        const aktiv = wert === f
        return (
          <button
            key={f}
            type="button"
            aria-pressed={aktiv}
            onClick={() => setzen(aktiv ? 'alle' : f)}
            className={`cursor-pointer rounded-full border px-2.5 py-[3px] text-[12px] ${
              aktiv
                ? 'border-sonne-line bg-sonne-bg font-semibold text-sonne'
                : 'border-line2 bg-surface text-ink2 hover:bg-[#fafaf8]'
            }`}
          >
            {MARKIER_FILTER_LABELS[f]}
            {f !== 'ohne' && <span className="tabular ml-1 font-semibold">{zahl}</span>}
          </button>
        )
      })}
    </div>
  )
}

/** Passt eine Aufgabe zum gesetzten Filter? Für Ansichten, die selbst filtern. */
export function useMarkierFilter(): (taskId: string, wert: MarkierFilter | 'alle') => boolean {
  const markierungen = useOffeneMarkierungen()
  const heute = heuteSchluessel()
  return (taskId, wert) =>
    wert === 'alle' || passtZuMarkierFilter(markierungen.get(taskId) ?? null, heute, wert)
}
